import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { X } from 'lucide-react';
import { FormModal, Field, FilePicker, inputCls, btnPrimary, btnGhost } from '../components/shared/FormKit';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import StatusActionsCard from '../components/workflow/StatusActionsCard';
import ApprovalsCard from '../components/workflow/ApprovalsCard';
import ReviewerSelect, { useClearOffRosterPick, MASTER_REVIEWER_HINT, reviewerPickOf } from '../components/workflow/ReviewerSelect';
import { formatDateTime } from '../utils/dateFormat';
import { firmToday } from '../utils/dateRange';
import SegmentedSwitch from '../components/shared/SegmentedSwitch';
import ChangesSinceReturnCard from '../components/workflow/ChangesSinceReturnCard';
import WorkflowStatusBadge from '../components/workflow/WorkflowStatusBadge';
import { SectionCard } from '../components/workflow/SectionCard';
import AttachmentsList from '../components/workflow/AttachmentsList';
import EditableAttachments, { useRemovableFiles } from '../components/workflow/EditableAttachments';
import HistoryCard from '../components/workflow/HistoryCard';
import RejectModal from '../components/workflow/RejectModal';
import ReturnCommentsBanner from '../components/workflow/ReturnCommentsBanner';
import FieldCommentAnchor from '../components/workflow/FieldCommentAnchor';
import { useFieldComments, FieldCommentsProvider, FieldCommentInline } from '../components/workflow/fieldComments';
import { AMENDMENT_FIELD_LABELS } from '../components/workflow/constants';
import { fmtAmendValueRich } from '../components/workflow/CoverageDisplay';
import NowName from '../components/shared/NowName';
import { fetchJSON, API_BASE_URL } from '../utils/api';

const WorkflowCarDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [modifying, setModifying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [detail, hist] = await Promise.all([
        fetchJSON(`/api/workflow/car/${id}`),
        fetchJSON(`/api/workflow/car/${id}/events`),
      ]);
      setData(detail);
      setEvents(hist.data || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (path, body) => {
    setBusy(true);
    setError(null);
    try {
      await fetchJSON(`/api/workflow/car/${id}/${path}`, {
        method: 'POST',
        body: JSON.stringify(body || {}),
      });
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const request = data?.request;
  const { currentEntity, review, canReview, canModify, canDelete } = data || {};
  const payloadEntries = request ? Object.entries(request.payload || {}) : [];
  const files = request?.files || [];

  // Live value for the drift hint (the master moved while this sat in review).
  const liveOf = (field) => {
    if (!currentEntity) return undefined;
    if (field === 'entityName') return currentEntity.entityName;
    if (field === 'uen') return currentEntity.uen;
    if (field === 'dateOfAppointment') return currentEntity.dateOfAppointment || null;
    if (field === 'active') return currentEntity.active;
    if (field === 'inactiveDate') return currentEntity.inactiveDate || null;
    if (field === 'inactiveReason') return currentEntity.inactiveReason || null;
    if (field === 'clientGroup') return currentEntity.clientGroupName;
    if (field === 'groupName') return currentEntity.clientGroupName;
    return undefined;
  };

  // 退件的逐項意見：能退件的人就能逐格留言，被退回的人就讀得到。
  const canComment = !!request && (!!review?.canRejectOnly && request.status === 'requested');
  const fc = useFieldComments({
    events,
    canComment,
    returned: !!request && request.status === 'awaiting_info',
    labels: AMENDMENT_FIELD_LABELS,
    amendment: true,
  });

  return (
    <RequestDetailShell
      backTo="/workflow/car"
      backLabel="Client Amendment"
      loading={!data}
      error={error}
    >
      {request && (
        <FieldCommentsProvider value={fc}>
          <RequestHeader
            requestNo={request.requestNo}
            typeLabel="Client Amendment"
            badge={<WorkflowStatusBadge status={request.status} />}
            submittedBy={request.requesterEmail}
            submittedAt={request.createdAt}
          />

          <StatusActionsCard
            sides={[{
              key: 'main', label: null, status: request.status,
              note: request.statusUpdatedAt ? formatDateTime(request.statusUpdatedAt) : null,
            }]}
            busy={busy}
            events={events}
            onApprove={canReview && request.status === 'requested' ? () => act('approve') : null}
            approveLabel={() => 'Approve & Apply'}
            onReject={canComment ? () => setRejecting(true) : null}
            onModify={canModify ? () => setModifying(true) : null}
            onResubmit={canModify && request.status === 'awaiting_info' ? () => act('resubmit') : null}
            onDelete={canDelete ? () => setDeleting(true) : null}
          />

          <ChangesSinceReturnCard
            events={events}
            amendment
            changeLabels={AMENDMENT_FIELD_LABELS}
            fmtChangeValue={fmtAmendValueRich}
          />

          <ApprovalsCard review={review} approvals={request.approvals || []} status={request.status} />

          <SectionCard title="Client Entity">
            <p className="text-sm text-slate-800 break-words"><NowName value={request.entityLabel} now={request.entityLabelNow} /></p>
            {!currentEntity && <p className="text-xs text-amber-600 mt-1">The entity no longer exists.</p>}
            <p className="text-xs text-slate-500 mt-2 break-words"><span className="font-medium text-slate-600">Reason:</span> {request.reason}</p>
          </SectionCard>

          <SectionCard title="Requested Changes">
            <table className="w-full table-fixed text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500">
                  <th className="px-3 py-1.5 font-medium w-1/3">Field</th>
                  <th className="px-3 py-1.5 font-medium w-1/3">Current</th>
                  <th className="px-3 py-1.5 font-medium w-1/3">Requested</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {payloadEntries.map(([field, diff]) => {
                  const live = liveOf(field);
                  const drifted = request.status === 'requested' && live !== undefined && String(live) !== String(diff.before);
                  return (
                    // group：逐列的評論入口平時藏著。
                    <tr key={field} className="group">
                      <td className="px-3 py-1.5 text-slate-700 align-top break-words">
                        {AMENDMENT_FIELD_LABELS[field] || field}
                        <FieldCommentAnchor
                          field={field}
                          label={AMENDMENT_FIELD_LABELS[field] || field}
                          className="mt-0.5"
                          compact
                          hoverOnly
                        />
                      </td>
                      <td className="px-3 py-1.5 text-slate-500 align-top break-words">
                        {fmtAmendValueRich(field, diff.before)}
                        {drifted && <span className="block text-xs text-amber-600">now: {String(live)} (changed since submission)</span>}
                      </td>
                      <td className="px-3 py-1.5 text-slate-900 font-medium align-top break-words">{fmtAmendValueRich(field, diff.after)}</td>
                    </tr>
                  );
                })}
                {payloadEntries.length === 0 && (
                  <tr><td colSpan={3} className="px-3 py-2 text-xs text-slate-500">No field changes — this amendment only updates the attached documents below.</td></tr>
                )}
              </tbody>
            </table>
          </SectionCard>

          {files.length > 0 && (
            <SectionCard title="Attachments">
              <AttachmentsList
                files={files}
                downloadHref={(f) => `${API_BASE_URL}/api/workflow/car/${request.id}/files/${f.id}`}
                badgeFor={(f) => (f.docType === 'GROUP_STRUCTURE'
                  ? ['Updated group structure chart', 'bg-violet-50 text-violet-700']
                  : ['Supporting', 'bg-slate-100 text-slate-600'])}
              />
              <FieldCommentAnchor field="files" label="Attachments" />
            </SectionCard>
          )}

          <HistoryCard
            events={events}
            actionLabels={{ applied: ['Applied to client master', 'bg-green-50 text-green-700'] }}
            changeLabels={AMENDMENT_FIELD_LABELS}
            fmtChangeValue={fmtAmendValueRich}
          />

          {rejecting && (
            <RejectModal
              requestNo={request.requestNo}
              fieldComments={fc}
              onClose={() => setRejecting(false)}
              onSubmit={async (reason, fieldComments) => { await act('reject', { reason, fieldComments }); setRejecting(false); fc.clear(); }}
            />
          )}

          {modifying && (
            <ModifyModal
              request={request}
              currentEntity={currentEntity}
              events={events}
              onClose={() => setModifying(false)}
              onDone={() => { setModifying(false); load(); }}
            />
          )}

          {deleting && (
            <ConfirmDeleteModal
              title={`Delete ${request.requestNo}`}
              message="Withdraw and delete this amendment request?"
              onClose={() => setDeleting(false)}
              onConfirm={async () => {
                await fetchJSON(`/api/workflow/car/${id}`, { method: 'DELETE' });
                navigate('/workflow/car');
              }}
            />
          )}
        </FieldCommentsProvider>
      )}
    </RequestDetailShell>
  );
};

// Modify = restate the requested field changes (entity name / relationship /
// client group name; UEN is shown, not edited), swap the attached documents and/or edit the reason. The backend
// rebuilds the payload from what is sent, so each box is prefilled with the
// requested value (falling back to the live one) and re-sent while it still
// differs from the master; setting it back to the current value drops that
// change. Moving the entity to ANOTHER group is deferred by design.
// Save only — re-submit is a separate step.
const ModifyModal = ({ request, currentEntity, events = [], onClose, onDone }) => {
  const [reason, setReason] = useState(request.reason || '');
  const [reviewerStaffId, setReviewerStaffId] = useState(
    reviewerPickOf(request)
  );
  // A named reviewer no longer on the roster is cleared and must be re-picked.
  useClearOffRosterPick(reviewerStaffId, setReviewerStaffId, null, 'master');
  const p = request.payload || {};
  const liveName = currentEntity?.entityName || '';
  const currentGroupName = currentEntity?.clientGroupName || '';
  const [entityName, setEntityName] = useState(p.entityName?.after || liveName);
  const liveRelationship = currentEntity && currentEntity.active === false ? 'inactive' : 'active';
  const [relationship, setRelationship] = useState(p.active ? (p.active.after ? 'active' : 'inactive') : liveRelationship);
  const [inactiveDate, setInactiveDate] = useState(p.inactiveDate?.after || firmToday());
  const [groupName, setGroupName] = useState(p.groupName?.after || currentGroupName);
  const nameTo = entityName.trim() && entityName.trim() !== liveName ? entityName.trim() : '';
  const relationshipTo = currentEntity && relationship !== liveRelationship ? relationship : '';
  const inProgress = currentEntity?.inProgressEngagementCount || 0;
  const inactiveBlocked = relationshipTo === 'inactive' && inProgress > 0;
  const renameTo = groupName.trim() && groupName.trim() !== currentGroupName ? groupName.trim() : '';
  const [files, setFiles] = useState([]);
  const { removeIds, toggle, appendTo } = useRemovableFiles();
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setError(null);
    if (!reviewerStaffId) { setError('Pick the reviewer who will approve this request.'); return; }
    setSaving(true);
    try {
      const body = new FormData();
      if (reason.trim()) body.set('reason', reason.trim());
      body.set('reviewerStaffId', reviewerStaffId);
      if (nameTo) body.set('entityName', nameTo);
      if (relationshipTo) {
        body.set('relationship', relationshipTo);
        if (relationshipTo === 'inactive') body.set('inactiveDate', inactiveDate);
      }
      if (renameTo) body.set('groupName', renameTo);
      files.forEach((x) => body.append('files', x));
      appendTo(body);
      await fetchJSON(`/api/workflow/car/${request.id}`, { method: 'PATCH', body });
      onDone();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormModal
      title={`Modify ${request.requestNo}`}
      size="lg"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={save} disabled={saving || inactiveBlocked} className={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
        </>
      )}
    >
      <ReturnCommentsBanner events={events} />
      {error && (
        <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} className="ml-auto"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}
      <p className="text-sm text-slate-600 break-words">{request.entityLabel}</p>
      {currentEntity && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Entity Name" field="entityName" hint={nameTo ? `Currently "${liveName}"` : undefined}>
            <input type="text" value={entityName} onChange={(e) => setEntityName(e.target.value)} className={inputCls} />
            <FieldCommentInline field="entityName" />
          </Field>
          <Field label="UEN">
            <p className="text-sm text-slate-700 font-mono py-2">{currentEntity.uen || '—'}</p>
          </Field>
          <div className="md:col-span-2">
            <Field
              label="Relationship"
              field="active"
              hint={relationshipTo === 'inactive'
                ? 'The Reason below is recorded as the inactive reason on the client master and in its history.'
                : relationshipTo === 'active' ? 'Approval reactivates the company.' : undefined}
            >
              <SegmentedSwitch
                ariaLabel="Relationship"
                options={[{ key: 'active', label: 'Active' }, { key: 'inactive', label: 'Inactive' }]}
                value={relationship}
                onChange={setRelationship}
              />
              {relationshipTo === 'inactive' && (
                <div className="mt-3 max-w-xs">
                  <Field label="Inactive Date" required field="inactiveDate">
                    <input type="date" value={inactiveDate} max={firmToday()} onChange={(e) => setInactiveDate(e.target.value)} className={inputCls} />
                  </Field>
                </div>
              )}
              {inactiveBlocked && (
                <p className="mt-2 text-sm text-amber-700">{inProgress} engagement{inProgress === 1 ? ' is' : 's are'} still in progress — the company cannot be marked inactive until closed.</p>
              )}
              <FieldCommentInline field="active" />
              <FieldCommentInline field="inactiveReason" />
            </Field>
          </div>
          {currentGroupName && (
            <div className="md:col-span-2">
              <Field
                label="Client Group Name"
                field="groupName"
                hint={renameTo ? `Currently "${currentGroupName}" — set it back to drop the rename.` : 'Rename the group this company belongs to.'}
              >
                <input type="text" value={groupName} onChange={(e) => setGroupName(e.target.value)} className={inputCls} />
                <FieldCommentInline field="groupName" />
              </Field>
            </div>
          )}
        </div>
      )}
      <EditableAttachments
        files={request.files || []}
        removeIds={removeIds}
        onToggle={toggle}
        downloadHref={(f) => `${API_BASE_URL}/api/workflow/car/${request.id}/files/${f.id}`}
        badgeFor={(f) => (f.docType === 'GROUP_STRUCTURE'
          ? ['Group structure', 'bg-purple-50 text-purple-700']
          : f.docType === 'APPOINTMENT_LETTER'
            ? ['Appointment letter', 'bg-emerald-50 text-emerald-700']
            : ['Supporting', 'bg-slate-100 text-slate-600'])}
        label="Files on This Amendment"
      />
      <FieldCommentInline field="files" />
      <Field label="Add More Supporting Files">
        <FilePicker
          multiple
          kind="docsAndImages"
          onChange={(e) => setFiles(Array.from(e.target.files || []))}
        />
      </Field>
      <Field label="Reason" field="reason" required>
        <textarea rows="2" value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} />
      </Field>
      <Field label="Reviewer" field="reviewerName" required hint={MASTER_REVIEWER_HINT}>
        <ReviewerSelect roster="master" value={reviewerStaffId} onChange={setReviewerStaffId} />
      </Field>
    </FormModal>
  );
};

export default WorkflowCarDetail;
